import React, { useEffect, useMemo } from 'react';
import { Select } from 'antd';
import {
  Card,
  CurrencyInput,
  ErrorHandler,
  Gap,
  Placeholder,
  Text,
  Title,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { Colors } from '@common/tokens';
import { BULK_WORKFORCE_GROUP_SHIFT_GAP_OVERRIDE_PREVIEW_QUERY } from '@scheduling/graphql/queries/bulk-shift-incentive-override';
import {
  buildDisabledHelperCopy,
  buildFreeFormRateRangeHint,
  buildOptionLabel,
  buildOptionValueKey,
  buildSelectFromUniformDefault,
  findOptionByValueKey,
  selectionToValueKey,
} from './_bulk-shift-step-set-tiers-service';
import type {
  BulkOverridePreviewRowType,
  SelectedGapEntryType,
  Step2SelectionsType,
  WorkforceGroupOverrideSelectionType,
} from './_bulk-shift-step-types';
import { iterationMap } from 'works/legacy/services/iteration-service';

// One row per workforce group with the control branch driven entirely by the
// server-authoritative `controlMode`. The wizard never re-derives intersection
// length / disabled reasons in TS — see plan §"Wizard steps (detailed) - Step 2".
export function BulkShiftStepSetTiers({
  selectedGaps,
  selections,
  onSelectionsChange,
}: {
  selectedGaps: SelectedGapEntryType[];
  selections: Step2SelectionsType;
  onSelectionsChange: (next: Step2SelectionsType) => void;
}) {
  const shiftGapIds = useMemo(
    () => selectedGaps.map((gap) => gap.shiftGapId),
    [selectedGaps]
  );

  const { data, loading, error } = useQuery(
    BULK_WORKFORCE_GROUP_SHIFT_GAP_OVERRIDE_PREVIEW_QUERY,
    {
      variables: { shiftGapIds },
      fetchPolicy: 'network-only',
    }
  );

  const previewRows = data?.bulkWorkforceGroupShiftGapOverridePreview;

  // Seed the Select branches with the server's `uniformDefault` once per
  // preview load. Free-form rows intentionally open empty (no pre-fill).
  // Re-seeding only runs when the WFG row count or workforceGroupIds change
  // so the user's edits aren't clobbered on re-render.
  useEffect(() => {
    if (!previewRows) return;

    const seededEntries = previewRows.reduce<Step2SelectionsType>(
      (acc, row) => {
        const wfgId = row.workforceGroup.id;
        if (selections[wfgId]) return acc;

        const seeded = buildSelectFromUniformDefault(row);
        if (seeded) acc[wfgId] = seeded;
        return acc;
      },
      {}
    );

    if (Object.keys(seededEntries).length > 0) {
      onSelectionsChange({ ...selections, ...seededEntries });
    }
    // We intentionally exclude `selections` and `onSelectionsChange` from the
    // dependency list — the seed should only run when the preview rows change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRows]);

  if (error) {
    return <ErrorHandler error={error} log="BulkShiftSetTiersStep" />;
  }

  function updateSelection(
    workforceGroupId: string,
    next: WorkforceGroupOverrideSelectionType | null
  ) {
    const updated: Step2SelectionsType = { ...selections };
    if (next === null) {
      delete updated[workforceGroupId];
    } else {
      updated[workforceGroupId] = next;
    }
    onSelectionsChange(updated);
  }

  return (
    <Gap vertical size="md" fullWidth align="stretch">
      <Gap vertical size="md">
        <Title size="h4">Set incentive rates per workforce group</Title>
        <Text>
          Choose a rate for each workforce group. The selected rate will apply
          to all selected shifts. Groups left unset will be skipped.
        </Text>
      </Gap>

      {loading ? (
        iterationMap(5, (key) => (
          <BulkShiftWorkforceGroupRowSkeleton key={key} />
        ))
      ) : previewRows?.length === 0 ? (
        <Text secondary>
          No workforce groups are eligible for the selected shifts. Adjust your
          Step 1 selection and try again.
        </Text>
      ) : (
        previewRows?.map((row) => (
          <BulkShiftWorkforceGroupRow
            key={row.id}
            row={row}
            selection={selections[row.workforceGroup.id]}
            onChange={(next) => updateSelection(row.workforceGroup.id, next)}
          />
        ))
      )}
    </Gap>
  );
}

function BulkShiftWorkforceGroupRowSkeleton() {
  return (
    <Card>
      <Gap vertical size="sm" fullWidth align="stretch">
        <Placeholder loading paragraph={false} title={{ width: '30%' }}>
          <></>
        </Placeholder>
        <Placeholder.Input loading width={360}>
          <></>
        </Placeholder.Input>
      </Gap>
    </Card>
  );
}

// Encapsulates the per-WFG control. Splitting it from the parent keeps the
// useEffect dependency list manageable and lets each row own its own keying
// for the resolver / static / free-form branches.
function BulkShiftWorkforceGroupRow({
  row,
  selection,
  onChange,
}: {
  row: BulkOverridePreviewRowType;
  selection: WorkforceGroupOverrideSelectionType | undefined;
  onChange: (next: WorkforceGroupOverrideSelectionType | null) => void;
}) {
  return (
    <Card data-testid={`bulk-shift-set-tiers-row-${row.workforceGroup.id}`}>
      <Gap vertical size="sm" fullWidth align="stretch">
        <Text bold size="md">
          {row.workforceGroup.name}
        </Text>
        <BulkShiftWorkforceGroupControl
          row={row}
          selection={selection}
          onChange={onChange}
        />
      </Gap>
    </Card>
  );
}

function BulkShiftWorkforceGroupControl({
  row,
  selection,
  onChange,
}: {
  row: BulkOverridePreviewRowType;
  selection: WorkforceGroupOverrideSelectionType | undefined;
  onChange: (next: WorkforceGroupOverrideSelectionType | null) => void;
}) {
  if (row.controlMode === 'DISABLED') {
    return (
      <BulkShiftDisabledControl
        row={row}
        workforceGroupName={row.workforceGroup.name}
      />
    );
  }

  if (row.controlMode === 'FREE_FORM') {
    return (
      <BulkShiftFreeFormControl
        row={row}
        selection={selection}
        onChange={onChange}
      />
    );
  }

  return (
    <BulkShiftSelectControl
      row={row}
      selection={selection}
      onChange={onChange}
    />
  );
}

function BulkShiftDisabledControl({
  row,
  workforceGroupName,
}: {
  row: BulkOverridePreviewRowType;
  workforceGroupName: string;
}) {
  const helperCopy = buildDisabledHelperCopy(row);

  return (
    <Gap vertical size="xs">
      <Select
        disabled
        placeholder="Select rate"
        style={{ width: '100%', maxWidth: 360 }}
        data-testid={`bulk-shift-set-tiers-select-${row.workforceGroup.id}`}
        aria-label={`${workforceGroupName} rate`}
      />
      {helperCopy && (
        <Text>
          <span style={{ color: Colors.harvestGoldDarker }}>{helperCopy}</span>
        </Text>
      )}
    </Gap>
  );
}

function BulkShiftSelectControl({
  row,
  selection,
  onChange,
}: {
  row: BulkOverridePreviewRowType;
  selection: WorkforceGroupOverrideSelectionType | undefined;
  onChange: (next: WorkforceGroupOverrideSelectionType | null) => void;
}) {
  const sortedOptions = useMemo(
    () =>
      [...row.intersectedOptions].sort(
        (a, b) => a.incentiveCents - b.incentiveCents
      ),
    [row.intersectedOptions]
  );

  const handleChange = (valueKey: string | undefined) => {
    if (!valueKey) {
      onChange(null);
      return;
    }
    const option = findOptionByValueKey(row.intersectedOptions, valueKey);
    if (!option) return;

    onChange({
      workforceGroupId: row.workforceGroup.id,
      workforceGroupName: row.workforceGroup.name,
      incentiveCents: option.incentiveCents,
      moneyAmountCents: option.moneyAmountCents ?? null,
      controlMode: row.controlMode,
      displayAsLumpSumUniform: row.displayAsLumpSumUniform,
    });
  };

  return (
    <Select
      allowClear
      placeholder="Select rate"
      style={{ width: '100%', maxWidth: 360 }}
      value={selectionToValueKey(selection)}
      onChange={handleChange}
      dropdownMatchSelectWidth={false}
      data-testid={`bulk-shift-set-tiers-select-${row.workforceGroup.id}`}
      aria-label={`${row.workforceGroup.name} rate`}
    >
      {sortedOptions.map((option) => {
        const valueKey = buildOptionValueKey(
          option.incentiveCents,
          option.moneyAmountCents
        );
        return (
          <Select.Option key={valueKey} value={valueKey}>
            {buildOptionLabel({
              controlMode: row.controlMode,
              option,
              displayAsLumpSumUniform: row.displayAsLumpSumUniform,
            })}
          </Select.Option>
        );
      })}
    </Select>
  );
}

function BulkShiftFreeFormControl({
  row,
  selection,
  onChange,
}: {
  row: BulkOverridePreviewRowType;
  selection: WorkforceGroupOverrideSelectionType | undefined;
  onChange: (next: WorkforceGroupOverrideSelectionType | null) => void;
}) {
  const rangeHint = buildFreeFormRateRangeHint(row);

  const valueDollars =
    selection && selection.incentiveCents > 0
      ? selection.incentiveCents / 100
      : undefined;

  const handleChange = (input: number | string | null | undefined) => {
    if (input === null || input === undefined || input === '') {
      onChange(null);
      return;
    }

    const dollars = typeof input === 'number' ? input : Number(input);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      onChange(null);
      return;
    }

    const cents = Math.round(dollars * 100);
    onChange({
      workforceGroupId: row.workforceGroup.id,
      workforceGroupName: row.workforceGroup.name,
      incentiveCents: cents,
      moneyAmountCents: null,
      controlMode: 'FREE_FORM',
      displayAsLumpSumUniform: false,
    });
  };

  return (
    <Gap vertical size="xs" align="stretch">
      <CurrencyInput
        placeholder="Enter rate"
        value={valueDollars}
        style={{ width: '100%', maxWidth: 260 }}
        onChange={handleChange}
        data-testid={`bulk-shift-set-tiers-free-form-${row.workforceGroup.id}`}
        aria-label={`${row.workforceGroup.name} hourly rate`}
      />
      {rangeHint && <Text secondary>{rangeHint}</Text>}
    </Gap>
  );
}
